import { Autocomplete, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useField, useFormikContext } from "formik";
import { FC, useEffect, useState } from "react";
import { useGetListQuery } from "../../features/api/Api";
import { useSearchDebounce } from "../../hooks";
import { IOption, IOptionSimple } from "../../interfaces/Utils";
import { isExists, locale, params } from "../../utils";

interface IReferenceSelect {
  name: string;
  label: string;
  reference: string;
  query?: any;
  optionname: string;
  subOptionName?: string;
  customOptionName?: string;
  optionId?: string;
  recordname?: string;
  disabled?: boolean;
  localePrefix?: string;
  readOnly?: boolean;
  skip?: boolean;
  secondField?: IOptionSimple[] | undefined;
}

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#0063cc",
  },
  "& label.Mui-disabled": {
    color:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#0063cc",
  },
  ".MuiOutlinedInput-input.Mui-disabled": {
    textFillColor:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e5e5e5",
    },
    "&.Mui-disabled": {
      color:
        localStorage.getItem("dark") === "true"
          ? "rgba(255, 255, 255, 0.38)"
          : "rgba(0, 0, 0, 0.38)",
      fieldset: {
        borderColor:
          localStorage.getItem("dark") === "true"
            ? "rgba(255, 255, 255, 0.38)"
            : "rgba(0, 0, 0, 0.38)",
      },
    },
    "&:hover fieldset": {
      borderColor: "#0063cc",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#0063cc",
    },
  },
});

export function setFilter(
  query: any,
  search: string,
  id: string | number | null = null
) {
  let customQuery = {};
  if (query) {
    customQuery = { ...query };
  }

  if (search) {
    customQuery = { ...customQuery, name: search };
  }

  // Kaldırılması gerekli bence
  if (id && !Array.isArray(id)) {
    customQuery = { ...customQuery, id };
  }

  if (Object.keys(customQuery).length > 0) {
    return customQuery;
  } else {
    return {};
  }
}

const ReferenceArraySelectInput: FC<IReferenceSelect> = ({
  name,
  label,
  reference,
  query,
  optionname,
  subOptionName,
  customOptionName,
  optionId,
  recordname,
  disabled,
  localePrefix,
  readOnly,
  secondField,
  skip,
}) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);
  const [search] = useSearchDebounce();
  const [options, setOptions] = useState<any[]>([]);

  const [fetchParams, setFetchParams] = useState({
    resource: reference,
    params: {
      ...params,
      filter: setFilter(query, search, field.value),
      pagination: { pageNumber: 0, size: 1000 },
    },
  });

  const { data: currentData, isLoading } = useGetListQuery<any>(fetchParams, {
    skip: skip,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isExists(currentData?.data)) {
      let records: IOption[] = [];

      currentData.data.map((item: any) =>
        records.push({
          value:
            optionId && isExists(item[optionId]) ? item[optionId] : item.id,
          label: subOptionName
            ? `${item[optionname]} ${customOptionName} ${
                item[subOptionName] ?? ""
              }`
            : item[optionname],

          secondField:
            secondField && secondField.length > 0
              ? secondField.map((field: IOptionSimple) => ({
                  [field.name]: item[field.value],
                }))
              : null,
        })
      );

      if (isExists(localePrefix)) {
        records = locale(records, localePrefix);
      }

      setOptions(records);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  /*  function searchQueryHandler(event: any) {
    setSearchQuery(event.target.value);
  } */

  useEffect(() => {
    if (search) {
      setFetchParams((prev: any) => ({
        ...prev,
        resource: reference,
        params: {
          ...prev.params,
          filter: setFilter(query, search),
        },
      }));
    }

    if (query) {
      setFetchParams((prev: any) => ({
        ...prev,
        resource: reference,
        params: {
          ...prev.params,
          filter: setFilter(query, search),
        },
      }));
    }
  }, [search, setFetchParams, query, reference]);

  return (
    <Autocomplete
      value={
        isExists(field.value)
          ? options.filter((option) =>
              field.value
                .map((item: any) =>
                  recordname ? item[recordname as any] : item
                )
                .includes(option.value)
            )
          : []
      }
      onChange={(event, newValue) => {
        let value: any = [];

        newValue.forEach((item) => {
          if (recordname) {
            let obj: any = {};
            obj[recordname] = item.value;
            if (secondField && secondField.length > 0) {
              secondField.forEach((field: IOptionSimple) => {
                const existKey = item.secondField.find((itemField: any) =>
                  Object.keys(itemField).find(
                    (fieldKey: any) => fieldKey === field.name
                  )
                );
                if (existKey) obj[field.name] = existKey[field.name];
              });
            }
            value.push(obj);
          } else {
            value.push(item.value);
          }
        });

        setFieldValue(field.name, value);
      }}
      readOnly={Boolean(readOnly)}
      multiple
      disabled={disabled}
      loading={isLoading}
      id={field.name}
      options={options}
      filterSelectedOptions
      renderInput={(params) => (
        <CssTextField
          {...params}
          error={Boolean(meta.error)}
          helperText={meta.touched && meta.error && meta.error}
          label={label}
        />
      )}
    />
  );
};

export default ReferenceArraySelectInput;
